import type { FluencyLanternChallenge } from './FluencyLanternConfig.js';

export type FluencyCheckResult = 'phrases-incomplete' | 'repeat-ready' | 'word-needed' | 'word-mismatch' | 'complete';

export interface FluencyChallengeProgress {
  readonly readingPass: 1 | 2;
  readonly litPhraseIds: readonly string[];
  readonly selectedWord: string | null;
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export interface FluencyLanternSerializedState {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly completedChallengeIds: readonly string[];
  readonly challengeProgress: Readonly<Record<string, FluencyChallengeProgress>>;
}

function blank(): FluencyChallengeProgress {
  return { readingPass: 1, litPhraseIds: [], selectedWord: null, hintStage: 0, attempts: 0, complete: false };
}

function sanitize(challenge: FluencyLanternChallenge, value: unknown): FluencyChallengeProgress {
  const saved = value as Partial<FluencyChallengeProgress> | undefined;
  const ids = challenge.phrases.map(phrase => phrase.id);
  const lit = Array.isArray(saved?.litPhraseIds)
    ? saved.litPhraseIds.filter((id): id is string => typeof id === 'string' && ids.includes(id)) : [];
  const prefix = ids.slice(0, lit.length);
  const validLit = prefix.every((id, index) => lit[index] === id) ? [...new Set(lit)] : [];
  return {
    readingPass: saved?.readingPass === 2 ? 2 : 1,
    litPhraseIds: validLit,
    selectedWord: typeof saved?.selectedWord === 'string' && challenge.reviewWords.includes(saved.selectedWord) ? saved.selectedWord : null,
    hintStage: Math.max(0, Math.min(challenge.hints.length, Number(saved?.hintStage) || 0)),
    attempts: Math.max(0, Number(saved?.attempts) || 0),
    complete: saved?.complete === true,
  };
}

export class FluencyLanternState {
  private challengeIndex: number;
  private readonly progress: Record<string, FluencyChallengeProgress> = {};
  private readonly completed = new Set<string>();

  constructor(private readonly challenges: readonly FluencyLanternChallenge[], saved?: Partial<FluencyLanternSerializedState>) {
    if (challenges.length === 0) throw new RangeError('Fluency Lantern Path needs challenges.');
    this.challengeIndex = Math.max(0, Math.min(challenges.length - 1, Number(saved?.challengeIndex) || 0));
    challenges.forEach(challenge => { this.progress[challenge.id] = sanitize(challenge, saved?.challengeProgress?.[challenge.id]); });
    (saved?.completedChallengeIds ?? []).forEach(id => { if (challenges.some(challenge => challenge.id === id)) this.completed.add(id); });
  }

  get challenge() { return this.challenges[this.challengeIndex]; }
  get snapshot() { return this.progress[this.challenge.id]; }
  get mastered() { return this.completed.size === this.challenges.length; }
  get hasNextChallenge() { return this.challengeIndex < this.challenges.length - 1; }
  get allPhrasesLit() { return this.snapshot.litPhraseIds.length === this.challenge.phrases.length; }

  lightPhrase(id: string) {
    if (this.snapshot.complete || this.allPhrasesLit) return false;
    const expected = this.challenge.phrases[this.snapshot.litPhraseIds.length]?.id;
    if (id !== expected) return false;
    this.update({ litPhraseIds: [...this.snapshot.litPhraseIds, id] });
    return true;
  }

  chooseWord(word: string) {
    if (this.snapshot.readingPass !== 2 || !this.allPhrasesLit || !this.challenge.reviewWords.includes(word)) return false;
    this.update({ selectedWord: word }); return true;
  }

  check(): FluencyCheckResult {
    if (!this.allPhrasesLit) return 'phrases-incomplete';
    if (this.snapshot.readingPass === 1) {
      this.update({ readingPass: 2, litPhraseIds: [], selectedWord: null });
      return 'repeat-ready';
    }
    if (!this.snapshot.selectedWord) return 'word-needed';
    this.update({ attempts: this.snapshot.attempts + 1 });
    if (this.snapshot.selectedWord !== this.challenge.focusWord) return 'word-mismatch';
    this.update({ complete: true }); this.completed.add(this.challenge.id); return 'complete';
  }

  requestHint() {
    const index = Math.min(this.snapshot.hintStage, this.challenge.hints.length - 1);
    this.update({ hintStage: Math.min(this.challenge.hints.length, index + 1) });
    return this.challenge.hints[index];
  }

  nextChallenge() { if (!this.snapshot.complete || !this.hasNextChallenge) return false; this.challengeIndex += 1; return true; }
  resetCurrent() { if (this.snapshot.complete) return false; const { hintStage, attempts } = this.snapshot; this.progress[this.challenge.id] = { ...blank(), hintStage, attempts }; return true; }
  replay() { this.challengeIndex = 0; this.completed.clear(); this.challenges.forEach(challenge => { this.progress[challenge.id] = blank(); }); }

  serialize(): FluencyLanternSerializedState {
    return {
      version: 1, challengeIndex: this.challengeIndex, completedChallengeIds: [...this.completed],
      challengeProgress: Object.fromEntries(this.challenges.map(challenge => [challenge.id, { ...this.progress[challenge.id], litPhraseIds: [...this.progress[challenge.id].litPhraseIds] }])),
    };
  }

  private update(patch: Partial<FluencyChallengeProgress>) { this.progress[this.challenge.id] = { ...this.snapshot, ...patch }; }
}
